import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { JobProvider } from '../../contexts/JobContext';
import RejoinButton from './RejoinButton';
import ResumePrompt from './ResumePrompt';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../ui/Button';
import { LogOut, User, Settings, Bell, Users, BarChart3, FileText, Users2, Menu, X, ChevronLeft, ChevronRight, HelpCircle, Building2, Sun, Moon, Phone } from 'lucide-react';
import { Logo } from '../ui/Logo';
import SidebarNav, { NavItem } from '../ui/SidebarNav';
import { useState } from 'react';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const useCustomMenu = true;
  const [customColor] = useState<string>(() => {
    try {
      return localStorage.getItem('sidebarMenuColor') || '#d41616';
    } catch {
      return '#d41616';
    }
  });

  React.useEffect(() => {
    try { localStorage.setItem('sidebarMenuColor', customColor); } catch {}
  }, [customColor]);

  const customMenuVars = React.useMemo<React.CSSProperties>(() => {
    // Derive a few shades from the base color for gradient and borders
    // Simple alpha overlays using the same color
    const hex = customColor;
    // Convert hex to rgb
    const toRgb = (h: string): [number, number, number] => {
      const v = h.replace('#','');
      const n = v.length === 3 ? v.split('').map(ch => ch + ch).join('') : v;
      const r = parseInt(n.slice(0,2), 16);
      const g = parseInt(n.slice(2,4), 16);
      const b = parseInt(n.slice(4,6), 16);
      return [r,g,b];
    };
    const [r,g,b] = toRgb(hex);
    const rgba = (a: number) => `rgba(${r}, ${g}, ${b}, ${a})`;
    return {
      // Base colors for gradient stops and borders
      ['--menu-bg-start' as any]: rgba(0.28),
      ['--menu-bg-end' as any]: rgba(0.12),
      ['--menu-border' as any]: rgba(0.30),
      // Active state colors
      ['--menu-active-start' as any]: rgba(0.36),
      ['--menu-active-end' as any]: rgba(0.18),
      ['--menu-active-border' as any]: rgba(0.55),
      ['--menu-shadow' as any]: `0 10px 25px ${rgba(0.22)}`,
    } as React.CSSProperties;
  }, [customColor]);

  const handleLogout = () => {
    logout();
  };

  // Dynamic navigation items based on user role
  const getNavigationItems = () => {
    if (!user) return [];
    
    const dashboardPath = user.role === 'admin' ? '/admin' : (user.role === 'mis_coordinator' ? '/mis-cordinator' : `/${user.role}`);
    const baseItems = [
      { label: 'Dashboard', path: dashboardPath, icon: BarChart3 }
    ];
    
    
    // Helper to check if user has admin access (all:admin grants everything)
    const hasAdminAccess = user.scopes?.includes('all:admin');
    
    // Admin menus by granular scopes
    if (hasAdminAccess || user.scopes?.includes('user_management:access')) {
      baseItems.push({ label: 'User Management', path: '/admin/users', icon: Users });
    }
    // Client Management - removed duplicate, handled below
    if (hasAdminAccess || user.scopes?.includes('requirements_values:manage') || user.scopes?.includes('system:manage')) {
      baseItems.push({ label: 'Requirements Values', path: '/admin/requirement-values', icon: Settings });
    }
    if (hasAdminAccess || user.scopes?.includes('role_management:access') || user.scopes?.includes('admin:access')) {
      baseItems.push({ label: 'Role Access', path: '/admin/roles', icon: Settings });
    }
    
    if (hasAdminAccess || user.scopes?.includes('requirements:view') || user.scopes?.includes('requirements:add')) {
      baseItems.push(
        { label: 'Requirements', path: '/requirements', icon: FileText },
        { label: '— Create Requirement', path: '/requirements?create=1', icon: FileText },
        { label: '— View Requirements', path: '/requirements', icon: FileText }
      );
    }
    
    if (hasAdminAccess || user.scopes?.includes('candidates:view') || user.scopes?.includes('candidates:add')) {
      baseItems.push(
        { label: 'Candidates', path: '/candidates', icon: Users2 }
      );
    }
    
    if (hasAdminAccess || user.scopes?.includes('clients:view') || user.scopes?.includes('clients:add')) {
      baseItems.push(
        { label: 'Client Management', path: '/clients', icon: Building2 }
      );
    }

    // Workforce Insights (testing: visible to team lead and above)
    if (hasAdminAccess || ['team_lead', 'ops_head', 'ceo', 'admin'].includes(user.role)) {
      baseItems.push(
        { label: 'Workforce Insights', path: '/workforce-insights', icon: BarChart3 }
      );
    }

    // Call Recordings (visible to team lead and above)
    if (hasAdminAccess || ['team_lead', 'ops_head', 'ceo', 'admin'].includes(user.role)) {
      baseItems.push(
        { label: 'Call Recordings', path: '/call-recordings', icon: Phone }
      );
    }

    // Interviews (testing: visible to HR coordinator and team lead)
    // Commented out for now
    // if (['hr_coordinator', 'team_lead', 'recruiter', 'ops_head', 'ceo', 'admin'].includes(user.role)) {
    //   baseItems.push(
    //     { label: 'Interviews', path: '/interviews', icon: FileText }
    //   );
    // }

    // Reports menu (removed for recruiter sidebar per request)

    // Team lead tools
    if (hasAdminAccess || user.scopes?.includes('audit_params:view') || user.scopes?.includes('admin:access')) {
      baseItems.push(
        { label: 'Audit Params', path: '/team-lead/audit-params', icon: Settings }
      );
    }

    // Support - all:admin or support:access, or default for admins
    if (hasAdminAccess || user.scopes?.includes('support:access')) {
      baseItems.push({ label: 'Support', path: '/support', icon: HelpCircle });
    }
    
    return baseItems;
  };

  const navigationItems = getNavigationItems();
  const navItemsForSidebar: NavItem[] = React.useMemo(() => {
    const base: NavItem[] = [];
    for (const it of navigationItems) {
      if (it.label === 'Requirements') {
        // No submenu; click leads to list. Create icon will live in EdgeActions on the page.
        base.push({ label: it.label, path: '/requirements', icon: FileText });
      } else if (it.label === '— Create Requirement' || it.label === '— View Requirements') {
        // remove flat submenu entries
      } else {
        base.push({ label: it.label, path: it.path, icon: it.icon });
      }
    }
    return base;
  }, [navigationItems]);

  // Context-aware page title and subtitle
  const getPageTitle = (pathname: string) => {
    // Handle specific page routes
    if (pathname.includes('/requirements/') && pathname.includes('/candidates')) {
      return 'Requirement Candidates';
    }
    if (pathname.includes('/candidates/') && pathname.includes('/analysis/')) {
      return 'Candidate Analysis';
    }
    if (pathname.includes('/candidates/submit/')) {
      return 'Submit Candidate';
    }
    if (pathname.includes('/candidates/')) {
      return 'Candidate Details';
    }
    if (pathname.includes('/requirements/')) {
      return 'Requirement Details';
    }
    
    // Fallback to navigation items
    return navigationItems.find(item => item.path === pathname)?.label || 'Dashboard';
  };

  // Subtitle removed per design update

  const content = (
    <div className="min-h-screen bg-background-200 dark:bg-[#141414] flex pt-20">
      {/* Top Navigation Bar - fixed full width */}
      <header className="fixed top-0 left-0 right-0 w-full bg-white dark:bg-gray-800 border-b border-border-100 dark:border-gray-700 h-20 px-7 z-50">
        <div className="grid grid-cols-3 items-center h-full w-full">
          {/* Left: menu + logo */}
          <div className="flex items-center gap-5">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-text-primary"
                aria-label="Toggle navigation"
              >
                {isMobileMenuOpen ? <X className="w-7 h-7 text-text-secondary" /> : <Menu className="w-7 h-7 text-text-secondary" />}
              </button>
              {/* Mobile Logo */}
              <Logo 
                size="sm"
                variant="text-only"
                onClick={() => navigate(user ? `/${user.role === 'admin' ? 'admin' : user.role}` : '/')}
                className="md:hidden"
              />
            </div>
            <Logo 
              size="md"
              variant="product-primary"
              onClick={() => navigate(user ? `/${user.role === 'admin' ? 'admin' : user.role}` : '/')}
              className="hidden md:flex"
            />
          </div>

          {/* Center: page title */}
          <div className="flex items-center justify-center text-center">
            <h1 className="text-2xl font-semibold text-text-primary truncate">
              {location.pathname === '/profile' ? 'User Profile' : getPageTitle(location.pathname)}
            </h1>
          </div>

          {/* Right: actions */}
          <div className="flex items-center justify-end gap-4">
            <RejoinButton />
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              title="Notifications"
            >
              <Bell className="w-7 h-7 text-text-secondary" />
              <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              title={theme === 'dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
              onClick={toggleTheme}
            >
              {theme === 'dark' ? (
                <Sun className="w-7 h-7 text-text-secondary" />
              ) : (
                <Moon className="w-7 h-7 text-text-secondary" />
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              title="Appearance Settings"
              onClick={() => navigate('/settings/appearance')}
            >
              <Settings className="w-7 h-7 text-text-secondary" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              title="Profile"
              onClick={() => navigate('/profile')}
            >
              <User className="w-7 h-7 text-text-secondary" />
            </Button>
          </div>
        </div>
      </header>
      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`${isSidebarCollapsed ? 'w-16' : 'w-64'} fixed left-0 top-20 bottom-0 transition-all duration-300 ease-in-out glass-sidebar flex flex-col z-40 lg:translate-x-0 ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        {/* Navigation Items with Collapse Control and Greeting */}
        <nav className="flex-1 px-3 py-3">
          
          <div className="flex items-center justify-between mb-2">
            {!isSidebarCollapsed && (
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <User className="w-4 h-4 text-white" />
                </div>
                <span className="text-sm font-medium text-text-enterprise-primary truncate">Hi {user?.first_name || user?.email?.split('@')[0]}</span>
              </div>
            )}
            <button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className="p-1.5 rounded-md glass-button hover:shadow-glow-primary hover:-translate-y-0.5 transition-all duration-300"
              title={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {isSidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </button>
          </div>
          <SidebarNav
            items={navItemsForSidebar}
            isCollapsed={isSidebarCollapsed}
            onNavigate={(path) => navigate(path)}
            activePath={location.pathname}
            storageKey={`sidebar-open-groups:${user?.id || 'me'}`}
            useCustomMenu={useCustomMenu}
            customMenuVars={useCustomMenu ? customMenuVars : undefined}
          />
        </nav>

        {/* Logout Only */}
        <div className="p-4 border-t border-glass-border">
          <Button
            onClick={handleLogout}
            className={`w-full flex items-center px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors ${
              isSidebarCollapsed ? 'justify-center' : ''
            }`}
            title={isSidebarCollapsed ? 'Logout' : undefined}
            variant="ghost"
            size="sm"
          >
            <LogOut className={`w-5 h-5 ${isSidebarCollapsed ? '' : 'mr-3'}`} />
            {!isSidebarCollapsed && <span>Logout</span>}
          </Button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className={`relative flex-1 flex flex-col w-full overflow-hidden transition-all duration-300 ease-in-out ${isSidebarCollapsed ? 'ml-16' : 'ml-64'}`}>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white dark:bg-gray-800 border-b border-border-100 dark:border-gray-700">
            <div className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.label}
                    onClick={() => {
                      navigate(item.path);
                      setIsMobileMenuOpen(false);
                    }}
                    className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive 
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800' 
                        : 'text-text-primary hover:bg-gray-50 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium text-sm">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-[#141414] p-6">
          {children}
        </main>
        <ResumePrompt />
      </div>
    </div>
  );

  // Provide Job context so header can show rejoin button
  return <JobProvider userId={user?.id}>{content}</JobProvider>;
};

export default Layout;
